import { useEffect, useState } from "react";
import { Save, Globe } from "lucide-react";
import {
  Button, Input, Textarea, Label, FieldHint, Skeleton, useToast,
} from "@/components/ui";
import { ApiError } from "@/lib/api";
import { storeApi, type StoreSettings } from "./api";

/**
 * Editable storefront content — the store site reads these live via
 * /api/public/store/site, so every save is instantly visible (no rebuild).
 */
export function StoreSitePage() {
  const toast = useToast();
  const [settings, setSettings] = useState<StoreSettings | null>(null);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    announcement: "",
    announcement_enabled: true,
    hero_headline: "",
    hero_sub: "",
    cta_label: "",
    contact_email: "",
    contact_phone: "",
    instagram: "",
    facebook: "",
    tiktok: "",
    x: "",
    policy_shipping: "",
    policy_returns: "",
    policy_privacy: "",
    policy_terms: "",
  });
  const set = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    storeApi.settings()
      .then((r) => {
        setSettings(r.settings);
        const s = r.settings;
        const social = (s.social ?? {}) as Record<string, string>;
        const policies = (s.policies ?? {}) as Record<string, string>;
        setForm({
          announcement: (s.announcement as string) ?? "",
          announcement_enabled: s.announcement_enabled !== false,
          hero_headline: (s.hero_headline as string) ?? "",
          hero_sub: (s.hero_sub as string) ?? "",
          cta_label: (s.cta_label as string) ?? "",
          contact_email: (s.contact_email as string) ?? "",
          contact_phone: (s.contact_phone as string) ?? "",
          instagram: social.instagram ?? "",
          facebook: social.facebook ?? "",
          tiktok: social.tiktok ?? "",
          x: social.x ?? "",
          policy_shipping: policies.shipping ?? "",
          policy_returns: policies.returns ?? "",
          policy_privacy: policies.privacy ?? "",
          policy_terms: policies.terms ?? "",
        });
      })
      .catch((err) => toast.error(err instanceof ApiError ? err.message : "Failed to load site content"));
  }, [toast]);

  const save = async () => {
    setSaving(true);
    try {
      await storeApi.saveSettings({
        announcement: form.announcement,
        announcement_enabled: form.announcement_enabled,
        hero_headline: form.hero_headline,
        hero_sub: form.hero_sub,
        cta_label: form.cta_label,
        contact_email: form.contact_email,
        contact_phone: form.contact_phone,
        social: { instagram: form.instagram, facebook: form.facebook, tiktok: form.tiktok, x: form.x },
        policies: {
          shipping: form.policy_shipping,
          returns: form.policy_returns,
          privacy: form.policy_privacy,
          terms: form.policy_terms,
        },
      });
      toast.success("Saved — the store site updates instantly");
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  if (settings === null) {
    return <div className="space-y-3">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-16" />)}</div>;
  }

  return (
    <div className="max-w-3xl space-y-6">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-zinc-100">Site content</h1>
          <p className="mt-1 text-sm text-zinc-400">
            The words on your storefront. Changes go live the moment you save — no republish needed.
          </p>
        </div>
        <Button leftIcon={<Save className="h-4 w-4" />} onClick={save} loading={saving}>Save</Button>
      </div>

      <section className="space-y-4 rounded-xl border border-zinc-800 bg-zinc-900/40 p-5">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-zinc-200"><Globe className="h-4 w-4 text-rose-300" /> Announcement bar</h2>
        <div>
          <Input value={form.announcement} onChange={(e) => set("announcement", e.target.value)} placeholder="Free shipping on orders over $50" />
        </div>
        <label className="flex items-center gap-2 text-sm text-zinc-300">
          <input type="checkbox" checked={form.announcement_enabled} onChange={(e) => set("announcement_enabled", e.target.checked)} className="accent-rose-500" />
          Show the announcement bar
        </label>
      </section>

      <section className="space-y-4 rounded-xl border border-zinc-800 bg-zinc-900/40 p-5">
        <h2 className="text-sm font-semibold text-zinc-200">Home page hero</h2>
        <div>
          <Label htmlFor="s-head">Headline</Label>
          <Input id="s-head" value={form.hero_headline} onChange={(e) => set("hero_headline", e.target.value)} />
        </div>
        <div>
          <Label htmlFor="s-sub">Subheading</Label>
          <Textarea id="s-sub" rows={2} value={form.hero_sub} onChange={(e) => set("hero_sub", e.target.value)} />
        </div>
        <div className="w-56">
          <Label htmlFor="s-cta">Button label</Label>
          <Input id="s-cta" value={form.cta_label} onChange={(e) => set("cta_label", e.target.value)} placeholder="Shop now" />
        </div>
      </section>

      <section className="space-y-4 rounded-xl border border-zinc-800 bg-zinc-900/40 p-5">
        <h2 className="text-sm font-semibold text-zinc-200">Contact & social</h2>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor="s-email">Contact email</Label>
            <Input id="s-email" value={form.contact_email} onChange={(e) => set("contact_email", e.target.value)} placeholder="hello@yourstore.com" />
            <FieldHint>Shown in the footer and used as the reply-to on receipts.</FieldHint>
          </div>
          <div>
            <Label htmlFor="s-phone">Contact phone</Label>
            <Input id="s-phone" value={form.contact_phone} onChange={(e) => set("contact_phone", e.target.value)} placeholder="optional" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          {(["instagram", "facebook", "tiktok", "x"] as const).map((k) => (
            <div key={k}>
              <Label htmlFor={`s-${k}`}>{k === "x" ? "X (Twitter)" : k[0]!.toUpperCase() + k.slice(1)}</Label>
              <Input id={`s-${k}`} value={form[k]} onChange={(e) => set(k, e.target.value)} placeholder="https://…" />
            </div>
          ))}
        </div>
      </section>

      <section className="space-y-4 rounded-xl border border-zinc-800 bg-zinc-900/40 p-5">
        <h2 className="text-sm font-semibold text-zinc-200">Policies</h2>
        <FieldHint>Shown on your store's policy pages. Markdown works (## headings, lists, links).</FieldHint>
        {([
          ["policy_shipping", "Shipping policy"],
          ["policy_returns", "Returns & refunds"],
          ["policy_privacy", "Privacy policy"],
          ["policy_terms", "Terms of service"],
        ] as const).map(([key, label]) => (
          <div key={key}>
            <Label htmlFor={`s-${key}`}>{label}</Label>
            <Textarea id={`s-${key}`} rows={4} value={form[key]} onChange={(e) => set(key, e.target.value)} />
          </div>
        ))}
      </section>

      <div className="flex justify-end">
        <Button leftIcon={<Save className="h-4 w-4" />} onClick={save} loading={saving}>Save</Button>
      </div>
    </div>
  );
}
